/**
 * Rate Shop Data Filtering Utilities
 *
 * Provides client-side filtering functions for rate data by channel type
 * and data validation utilities for the dual rate tables enhancement.
 */

import _ from 'lodash';
import type { ShoppedHotel } from '@/graphql/types';
import { type DateSegment, dayRange } from '../../../helpers/date-helpers';
import { baseFormat, getRowClass } from '../../../renderers/base-format';
import { masterColumns } from '../../../renderers/master-columns';
import type { ColumnParams } from '../../../types/columns';
import type { FixMeLater } from '../../../types/fix-me-later-type';
import type { OtaiLowestRatesQuery } from '../../overview-table/gql/_gen_/rate-shop.gql';
import type { DailyRatesData } from '../context/report-data-context';

export type ChannelFilter = 'any' | 'branded';
export type RateType = 'lowest' | 'best-flex';

export type CellObject = {
  cls: string;
  dataKey: string;
  displayValue: string | null;
  meta?: any;
  stayDate: string;
  value: string | number | null;
};

export type HeaderObject = {
  columnSize: number;
  dataKey: string;
  dataSource?: string;
  meta?: any;
  title: string;
  tooltip: string;
  isVisible?: boolean;
};

// Removed filterByChannel function as channel filtering is no longer used

/**
 * Validates that rate data contains expected fields
 * @param rateData Single rate data object
 * @returns boolean indicating if data is valid
 */
export function validateRateData(rateData: any): boolean {
  return (
    rateData &&
    typeof rateData.stay_date === 'string' &&
    Array.isArray(rateData.rates) &&
    rateData.rates.every(
      (rate: any) =>
        rate.competitor_name &&
        (typeof rate.value === 'number' || typeof rate.best_flex === 'number'),
    )
  );
}

/**
 * Extracts rate value based on rate type
 * @param rate Individual rate object
 * @param rateType Type of rate to extract ('lowest' or 'best-flex')
 * @returns Rate value or null if not available
 */
export function extractRateValue(rate: any, rateType: RateType): number | null {
  if (!rate) return null;

  if (rateType === 'best-flex') {
    // Check if best_flex is a boolean (true means cancellable) or a number (rate value)
    if (typeof rate.best_flex === 'number') {
      return rate.best_flex;
    } else if (rate.best_flex === true) {
      return rate.value || null; // If best_flex is true, use the value
    }
    return rate.value || null; // Fallback to regular value
  }

  return rate.value || null;
}

/**
 * Filters out invalid or incomplete rate data
 * @param data Array of daily rate data
 * @returns Cleaned data array with only valid entries
 */
export function cleanRateData(data: any[]): any[] {
  if (!data || data.length === 0) return [];

  return data.filter(validateRateData);
}

/**
 * Column types for rate table
 */
export type ColumnType = 'price_index' | 'client' | 'competitor';

/**
 * Column info with sorting and display data
 */
export interface ColumnInfo {
  name: string;
  type: ColumnType;
  sortOrder: number;
  competitorId?: string;
  truncatedName?: string;
}

/**
 * Identifies if a rate is a Price Index
 */
export function isPriceIndex(rate: any): boolean {
  return rate.competitor_name === 'Price Index' || rate.hotelName === 'Price Index';
}

/**
 * Identifies if a rate is a Client rate
 * In the existing system, client rates have competitor_id === '0' (which maps to rates.0 dataKey)
 */
export function isClientRate(rate: any): boolean {
  return rate.competitor_id === '0' || rate.competitor_id === 0;
}

/**
 * Gets first two words from competitor name for chart display
 * Used for chart legends to show "Best Western" instead of full name
 */
export function getFirstTwoWords(name: string): string {
  if (!name) return name;

  const words = name.split(' ');
  if (words.length <= 2) {
    return name; // Return as-is if 2 words or less
  }

  return words.slice(0, 2).join(' ');
}

/**
 * Truncates competitor name aggressively for header display
 * Aims for max 8-10 characters to match 5-day outlook styling
 */
export function truncateCompetitorName(name: string): string {
  if (!name) return name;

  const words = name.split(' ');

  // For single word, truncate if too long
  if (words.length === 1) {
    return words[0].length > 10 ? words[0].substring(0, 8) : words[0];
  }

  // For multiple words, be more aggressive
  const firstWord = words[0];
  const secondWord = words[1];

  // If first word is short, try to add second word
  if (firstWord.length <= 4 && secondWord) {
    const combined = `${firstWord} ${secondWord}`;
    if (combined.length <= 10) {
      return combined;
    }
    // Abbreviate second word
    return `${firstWord} ${secondWord.substring(0, 3)}`;
  }

  // If first word is long, just use abbreviated first word
  if (firstWord.length > 8) {
    return firstWord.substring(0, 8);
  }

  // Use first word only
  return firstWord;
}

/**
 * Gets organized column information with proper sorting
 * @param data Array of daily rate data
 * @returns Array of column info sorted properly
 */
export function getOrganizedColumns(data: any[]): ColumnInfo[] {
  if (!data || data.length === 0) return [];

  const columnMap = new Map<string, ColumnInfo>();

  data.forEach((day) => {
    if (day.rates) {
      day.rates.forEach((rate: any) => {
        if (rate.competitor_name && !columnMap.has(rate.competitor_name)) {
          let type: ColumnType;
          let sortOrder: number;

          if (isPriceIndex(rate)) {
            type = 'price_index';
            sortOrder = 1; // Price Index first
          } else if (isClientRate(rate)) {
            type = 'client';
            sortOrder = 2; // Client second
          } else {
            type = 'competitor';
            // Sort competitors by competitor_id (numeric)
            const competitorId = rate.competitor_id ? parseInt(rate.competitor_id, 10) : 999999;
            sortOrder = 1000 + competitorId; // Start after client
          }

          // For client rates, always use "CLIENT" as the display name
          const truncatedName =
            type === 'competitor'
              ? truncateCompetitorName(rate.competitor_name)
              : type === 'client'
                ? 'CLIENT'
                : rate.competitor_name;

          columnMap.set(rate.competitor_name, {
            name: rate.competitor_name,
            type,
            sortOrder,
            competitorId: rate.competitor_id,
            truncatedName: truncatedName,
          });
        }
      });
    }
  });

  // Convert to array and sort by sortOrder
  return Array.from(columnMap.values()).sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Formats a rate value based on the column type
 * @param value The rate value
 * @param columnType The type of column (price_index, client, or competitor)
 * @returns Formatted string
 */
export function formatRateValue(value: number | null, columnType: ColumnType): string {
  if (value === null || value === undefined) return '-';

  if (columnType === 'price_index') {
    // Price index as decimal number with 2 decimal places
    return Number(value).toFixed(2);
  } else {
    // Currency formatting for client and competitor rates
    return `$${Number(value).toFixed(0)}`;
  }
}

/**
 * Gets unique competitor names from rate data
 * @param data Array of daily rate data
 * @returns Array of unique competitor names sorted by business logic
 */
export function getUniqueCompetitors(data: any[]): string[] {
  const columns = getOrganizedColumns(data);
  return columns.map((col) => col.name);
}

/**
 * Generates rate shop table data identical to useTable hook
 * @param params Object containing dailyRates, compSet, startDate, endDate
 * @returns Object with headers and rows arrays formatted for table display
 */
export function generateRateShopTable(params: {
  dailyRates: OtaiLowestRatesQuery['dailyRates'];
  compSet: OtaiLowestRatesQuery['compSet'];
  startDate: string;
  endDate: string;
}): DailyRatesData {
  const { dailyRates, compSet, startDate, endDate } = params;

  let headers: HeaderObject[] = [];
  let rows: CellObject[][] = [];

  if (dailyRates && !_.isEmpty(dailyRates) && compSet) {
    const data = { dailyRates, compSet };
    const tableCols = [
      { dataSource: 'dailyRates', key: 'stay_date' },
      ...compSet.filter((c) => c !== null),
    ];

    // Generate headers
    headers = tableCols
      .map((column) => {
        if (!column || !column.key) return null;

        const colDetails = getColDetails(
          column,
          compSet.filter((c) => c !== null),
        );
        return {
          columnSize: colDetails?.columnSize || 55,
          dataKey: column.key,
          meta: { ...column, ...colDetails },
          title: colDetails?.header || column.key,
          tooltip: (colDetails?.tooltip as string) || '',
        };
      })
      .filter((h) => h !== null) as HeaderObject[];

    // Generate date range
    const stayDates = dayRange(
      startDate,
      endDate,
      'day' as DateSegment,
      true, // includeSummary
    );

    // Generate rows
    rows = stayDates.map((stayDate, dateIdx) => {
      const isSummary = stayDate.length === 7; // Summary rows have format YYYY-MM

      const rowCells = tableCols.map((column) => {
        if (!column || !column.key) return null;

        const { key } = column;

        const colDetails = getColDetails(
          column,
          compSet.filter((c) => c !== null),
        );

        let rowCell: CellObject = {
          cls: getRowClass(stayDate, dateIdx, 'day', undefined, isSummary),
          dataKey: key,
          displayValue: null,
          meta: { ...column, ...colDetails, isSummary },
          stayDate,
          value: null,
        };

        if (colDetails) {
          const { dataSource } = colDetails;
          const categoryData = data[dataSource as keyof typeof data];

          if (dataSource === 'dailyRates' && categoryData && categoryData.length > 0) {
            let targetData: FixMeLater;
            let value: any;
            let rateData: any;

            if (isSummary) {
              targetData = _.filter(
                categoryData,
                (o: any) => o.date_ym === stayDate && o.stay_date !== null,
              );
            } else {
              targetData = _.find(categoryData, { stay_date: stayDate });
            }

            if (targetData) {
              const rateDetails = colDetails as FixMeLater;
              const rateTargetData = targetData as FixMeLater;
              const compId = rateDetails.competitor_id;

              if (key === 'stay_date') {
                if (isSummary) {
                  // Summary row sorting helper - add -999 to month string
                  value = `${stayDate}-999`;
                } else {
                  value = stayDate;
                }
              } else {
                // Extract rate data for this competitor
                const rates =
                  rateTargetData.rates ||
                  (Array.isArray(rateTargetData)
                    ? rateTargetData.flatMap((d: any) => d.rates || [])
                    : []);
                rateData = _.find(rates, { competitor_id: compId });

                if (isSummary) {
                  // For summary rows, use the rate value
                  value = rateData?.rate;
                } else {
                  value = rateData?.rate;
                }
              }

              const clientRate = rateData?.clientRate;

              rowCell = {
                cls: getRowClass(stayDate, dateIdx, 'day', value, isSummary, clientRate, key),
                meta: {
                  ...rowCell.meta,
                  ...column,
                  ...colDetails,
                  isSummary,
                  data: rateData,
                },
                dataKey: key,
                displayValue: String(baseFormat(key, value, 'day', colDetails, isSummary)) ?? null,
                stayDate,
                value,
              };
            }
          }
        }

        return rowCell;
      });

      return rowCells as CellObject[];
    });

    // Remove empty rows
    _.remove(rows, (row) => _.compact(row).length === 0);
  }

  return { headers: headers.map((h) => h.title), rows };
}

/**
 * Helper function to get column details for table generation
 * Extracted from useTable hook to maintain consistency
 */
function getColDetails(
  column: ShoppedHotel,
  compSet?: NonNullable<OtaiLowestRatesQuery['compSet']>,
) {
  const { key, dataSource, header, tooltip } = column;

  if (!key) return {} as ColumnParams;

  const masterColumn = masterColumns[key];

  if (key.includes('rates.')) {
    return { ..._.find(compSet, { key }), ...masterColumn };
  } else {
    if (dataSource || header || tooltip) {
      return {
        ...masterColumn,
        ...(dataSource && { dataSource }),
        ...(header && { header }),
        ...(tooltip && { tooltip }),
      } as ColumnParams;
    } else {
      return masterColumn as ColumnParams;
    }
  }
}
